import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Clock,
  Plus,
  Trash2,
  Edit2,
  Zap,
  GripVertical,
  Pause,
  Play,
  Shuffle,
  Loader2,
  Calendar,
  Image as ImageIcon,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useThemeStore } from '../../store/themeStore';
import { queueAPI, getErrorMessage } from '../../services/api';

// ─── Types ───
interface TimeSlot {
  id: string;
  time: string;
  enabled: boolean;
}

interface QueueSettings {
  active_days: string[];
  time_slots: TimeSlot[];
  timezone: string;
  paused: boolean;
  auto_schedule: boolean;
}

interface QueuePost {
  id: string;
  text: string;
  target_accounts: string[];
  target_platforms: string[];
  scheduled_at: string | null;
  media: Array<{ url: string; media_type: string; thumbnail_url?: string }>;
  status: string;
  created_at: string;
}

// ─── Constants ───
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Berlin', label: 'Central Europe (CET)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Tokyo', label: 'Japan (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
];

const PLATFORM_COLORS: Record<string, string> = {
  instagram: '#E4405F',
  facebook: '#1877F2',
  twitter: '#1DA1F2',
  linkedin: '#0A66C2',
  tiktok: '#000000',
  youtube: '#FF0000',
  pinterest: '#E60023',
  telegram: '#0088cc',
  threads: '#000000',
  bluesky: '#0085FF',
};

const STATUS_CONFIG: Record<string, { dot: string; label: string }> = {
  scheduled: { dot: '#f59e0b', label: 'Queued' },
  publishing: { dot: '#3b82f6', label: 'Publishing' },
  published: { dot: '#10b981', label: 'Published' },
  failed: { dot: '#ef4444', label: 'Failed' },
};

// ─── Settings Sidebar (Notion-style property rows) ───
interface SettingsSidebarProps {
  settings: QueueSettings;
  onSave: (updates: Partial<QueueSettings>) => void;
  isSaving: boolean;
}

const SettingsSidebar: React.FC<SettingsSidebarProps> = ({ settings, onSave, isSaving }) => {
  const { t } = useTranslation();
  const [localDays, setLocalDays] = useState(settings.active_days);
  const [localSlots, setLocalSlots] = useState<TimeSlot[]>(settings.time_slots);
  const [localTz, setLocalTz] = useState(settings.timezone);
  const [localAutoSchedule, setLocalAutoSchedule] = useState(settings.auto_schedule ?? false);
  const [newTime, setNewTime] = useState('');
  const [dirty, setDirty] = useState(false);

  const toggleDay = (day: string) => {
    setLocalDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    );
    setDirty(true);
  };

  const addSlot = () => {
    if (!newTime) { return; }
    if (localSlots.some((s) => s.time === newTime)) {
      toast.error(t('queue_time_slot_exists', 'This time slot already exists'));
      return;
    }
    setLocalSlots([...localSlots, { id: `slot-${Date.now()}`, time: newTime, enabled: true }]);
    setNewTime('');
    setDirty(true);
  };

  const removeSlot = (id: string) => {
    setLocalSlots(localSlots.filter((s) => s.id !== id));
    setDirty(true);
  };

  const toggleSlot = (id: string) => {
    setLocalSlots(localSlots.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s)));
    setDirty(true);
  };

  const handleSave = () => {
    const sorted = [...localSlots].sort((a, b) => a.time.localeCompare(b.time));
    onSave({ active_days: localDays, time_slots: sorted, timezone: localTz, auto_schedule: localAutoSchedule });
    setDirty(false);
  };

  return (
    <aside className="w-60 flex-shrink-0 border-r border-slate-100 bg-white overflow-y-auto">
      <div className="px-4 py-5">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-4">
          {t('queue_settings_title', 'Queue Settings')}
        </h2>

        {/* Active Days */}
        <section className="mb-5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
            {t('queue_active_days', 'Active Days')}
          </p>
          <div className="space-y-0.5">
            {DAYS.map((day) => {
              const checked = localDays.includes(day);
              const inputId = `day-${day.toLowerCase()}`;
              return (
                <div key={day} className="flex items-center gap-2 -mx-1 px-1 py-1 rounded hover:bg-slate-50">
                  <input
                    id={inputId}
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleDay(day)}
                    className="w-3.5 h-3.5 rounded-sm border-slate-300 text-slate-900 focus:ring-0 focus:ring-offset-0 cursor-pointer"
                  />
                  <label
                    htmlFor={inputId}
                    className={`text-[13px] cursor-pointer select-none ${checked ? 'text-slate-900' : 'text-slate-500'}`}
                  >
                    {day}
                  </label>
                </div>
              );
            })}
          </div>
        </section>

        {/* Posting Times */}
        <section className="mb-5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
            {t('queue_posting_times', 'Posting Times')}
          </p>
          <div className="space-y-0.5 mb-2">
            {[...localSlots]
              .sort((a, b) => a.time.localeCompare(b.time))
              .map((slot) => {
                const slotInputId = `slot-${slot.id}`;
                return (
                  <div
                    key={slot.id}
                    className="flex items-center gap-2 -mx-1 px-1 py-1 rounded hover:bg-slate-50 group"
                  >
                    <input
                      id={slotInputId}
                      type="checkbox"
                      checked={slot.enabled}
                      onChange={() => toggleSlot(slot.id)}
                      className="w-3.5 h-3.5 rounded-sm border-slate-300 text-slate-900 focus:ring-0 focus:ring-offset-0 cursor-pointer"
                    />
                    <Clock className="w-3 h-3 text-slate-400" />
                    <label
                      htmlFor={slotInputId}
                      className={`text-[13px] flex-1 cursor-pointer select-none tabular-nums ${
                        slot.enabled ? 'text-slate-900' : 'text-slate-400 line-through'
                      }`}
                    >
                      {slot.time}
                    </label>
                    <button
                      type="button"
                      onClick={() => removeSlot(slot.id)}
                      aria-label={t('queue_remove_slot', 'Remove time slot')}
                      className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-opacity"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
          </div>

          <div className="flex items-center gap-1.5 -mx-1 px-1">
            <label htmlFor="queue-new-time" className="sr-only">
              {t('queue_add_time', 'Add time')}
            </label>
            <input
              id="queue-new-time"
              type="time"
              value={newTime}
              onChange={(e) => setNewTime(e.target.value)}
              className="flex-1 px-2 py-1 border border-slate-200 rounded text-[12px] focus:outline-none focus:border-slate-400 tabular-nums"
            />
            <button
              type="button"
              onClick={addSlot}
              aria-label={t('queue_add_time_slot', 'Add time slot')}
              className="p-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        </section>

        {/* Smart Auto-Schedule */}
        <section className="mb-5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
            {t('queue_smart_schedule', 'Smart Schedule')}
          </p>
          <div className="flex items-start gap-2.5 -mx-1 px-1 py-1 rounded hover:bg-slate-50">
            <button
              type="button"
              role="switch"
              aria-checked={localAutoSchedule}
              aria-label={t('queue_ai_optimal_times', 'AI Optimal Times')}
              onClick={() => {
                setLocalAutoSchedule(!localAutoSchedule);
                setDirty(true);
              }}
              className={`relative shrink-0 w-7 h-4 rounded-full transition-colors ${
                localAutoSchedule ? 'bg-slate-900' : 'bg-slate-300'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                  localAutoSchedule ? 'translate-x-3' : ''
                }`}
              />
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] text-slate-900 leading-tight">
                {t('queue_ai_optimal_times', 'AI Optimal Times')}
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                {t('queue_auto_pick_times', 'Auto-pick best posting times based on your engagement data')}
              </p>
            </div>
          </div>
        </section>

        {/* Timezone */}
        <section className="mb-5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
            {t('queue_timezone', 'Timezone')}
          </p>
          <label htmlFor="queue-timezone" className="sr-only">
            {t('queue_timezone', 'Timezone')}
          </label>
          <select
            id="queue-timezone"
            value={localTz}
            onChange={(e) => {
              setLocalTz(e.target.value);
              setDirty(true);
            }}
            className="w-full px-2 py-1.5 -ml-0.5 bg-transparent border border-transparent hover:border-slate-200 rounded text-[13px] text-slate-900 focus:outline-none focus:border-slate-400"
          >
            {TIMEZONES.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </section>

        {dirty && (
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="w-full px-3 py-1.5 rounded text-[12px] font-medium bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 flex items-center justify-center gap-1.5"
          >
            {isSaving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {t('queue_save_settings', 'Save settings')}
          </button>
        )}
      </div>
    </aside>
  );
};

// ─── Post Row (Notion-style flat row) ───
interface PostRowProps {
  post: QueuePost;
  pc: string;
  onPublishNow: () => void;
  onRemove: () => void;
  isPublishing: boolean;
  isRemoving: boolean;
}

const PostRow: React.FC<PostRowProps> = ({ post, pc, onPublishNow, onRemove, isPublishing, isRemoving }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const scheduledDate = post.scheduled_at ? new Date(post.scheduled_at) : null;
  const statusCfg = STATUS_CONFIG[post.status] || STATUS_CONFIG.scheduled;

  const timeLabel = scheduledDate
    ? scheduledDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : null;
  const dayLabel = scheduledDate
    ? scheduledDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    : null;

  const thumbUrl = post.media[0]?.thumbnail_url || post.media[0]?.url;
  const isImage = post.media[0]?.media_type === 'image';
  const captionText = (post.text || '').trim();

  return (
    <div
      className="group flex items-center gap-3 pl-5 pr-3 py-2 -mx-3 rounded hover:bg-slate-50 cursor-pointer"
      onClick={() => navigate(`/compose/${post.id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          navigate(`/compose/${post.id}`);
        }
      }}
    >
      {/* Drag handle */}
      <span
        aria-hidden="true"
        className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-slate-500 transition-opacity flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-3.5 h-3.5" />
      </span>

      {/* Thumbnail */}
      <div className="w-7 h-7 rounded bg-slate-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
        {thumbUrl && isImage ? (
          <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <ImageIcon className="w-3.5 h-3.5 text-slate-300" />
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <p className="text-[13px] text-slate-900 truncate leading-snug">
          {captionText || (
            <span className="text-slate-400 italic">{t('queue_no_caption', 'No caption')}</span>
          )}
        </p>
        <div className="flex items-center gap-1.5 text-[11px] text-slate-400 mt-0.5 flex-wrap">
          {post.target_platforms.length > 0 ? (
            post.target_platforms.map((p, idx) => (
              <React.Fragment key={p}>
                {idx > 0 && <span className="text-slate-300">·</span>}
                <span className="inline-flex items-center gap-1">
                  <span
                    aria-hidden="true"
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: PLATFORM_COLORS[p.toLowerCase()] || '#94a3b8' }}
                  />
                  <span className="capitalize">{p}</span>
                </span>
              </React.Fragment>
            ))
          ) : (
            <span className="italic">{t('queue_no_platform', 'No platform')}</span>
          )}
          <span className="text-slate-300">•</span>
          <span className="inline-flex items-center gap-1">
            <span
              aria-hidden="true"
              className="w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: statusCfg.dot }}
            />
            <span>{statusCfg.label}</span>
          </span>
        </div>
      </div>

      {/* Scheduled time */}
      {dayLabel && timeLabel && (
        <div className="text-[11px] text-slate-400 tabular-nums flex-shrink-0 hidden sm:block">
          {dayLabel}, {timeLabel}
        </div>
      )}

      {/* Actions */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        {post.status === 'scheduled' && (
          <>
            <button
              type="button"
              onClick={onPublishNow}
              disabled={isPublishing}
              aria-label={t('queue_publish_now', 'Publish now')}
              title={t('queue_publish_now', 'Publish now')}
              className="p-1 rounded text-slate-500 hover:text-slate-900 hover:bg-white disabled:opacity-50"
              style={isPublishing ? { color: pc } : undefined}
            >
              {isPublishing ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Zap className="w-3.5 h-3.5" />
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/compose/${post.id}`)}
              aria-label={t('queue_edit', 'Edit')}
              title={t('queue_edit', 'Edit')}
              className="p-1 rounded text-slate-500 hover:text-slate-900 hover:bg-white"
            >
              <Edit2 className="w-3.5 h-3.5" />
            </button>
          </>
        )}
        <button
          type="button"
          onClick={onRemove}
          disabled={isRemoving}
          aria-label={t('queue_remove', 'Remove')}
          title={t('queue_remove', 'Remove')}
          className="p-1 rounded text-slate-500 hover:text-red-600 hover:bg-white disabled:opacity-50"
        >
          {isRemoving ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Trash2 className="w-3.5 h-3.5" />
          )}
        </button>
      </div>
    </div>
  );
};

// ─── Date Group (collapsible) ───
interface DateGroupProps {
  dateKey: string;
  label: string;
  count: number;
  children: React.ReactNode;
}

const DateGroup: React.FC<DateGroupProps> = ({ label, count, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 w-full text-left py-1 group"
        aria-expanded={open}
      >
        <span className="text-slate-300 group-hover:text-slate-500 transition-colors">
          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {label}
        </span>
        <span className="text-[11px] text-slate-300 ml-1">·</span>
        <span className="text-[11px] text-slate-400 tabular-nums">{count}</span>
      </button>
      {open && <div className="mt-0.5">{children}</div>}
    </div>
  );
};

// ─── Main QueuePage ───
const QueuePage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const pc = theme.hex?.primary || '#10b981';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Fetch queue settings
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['queue-settings'],
    queryFn: async () => {
      const { data } = await queueAPI.getSettings();
      return data as QueueSettings;
    },
    staleTime: 60_000,
  });

  // Fetch queue posts
  const { data: postsData, isLoading: postsLoading } = useQuery({
    queryKey: ['queue-posts'],
    queryFn: async () => {
      const { data } = await queueAPI.listPosts();
      return data as { posts: QueuePost[]; total: number };
    },
    staleTime: 60_000,
    refetchInterval: 2 * 60_000,
    refetchIntervalInBackground: false,
  });

  // Save settings mutation
  const saveSettingsMutation = useMutation({
    mutationFn: (updates: Partial<QueueSettings>) => queueAPI.updateSettings(updates as Record<string, unknown>),
    onSuccess: () => {
      toast.success(t('queue_settings_saved', 'Queue settings saved'));
      queryClient.invalidateQueries({ queryKey: ['queue-settings'] });
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e, t('queue_save_failed', 'Failed to save settings'))),
  });

  // Pause/resume mutation
  const togglePauseMutation = useMutation({
    mutationFn: (paused: boolean) => (paused ? queueAPI.resume() : queueAPI.pause()),
    onSuccess: (_, wasPaused) => {
      toast.success(wasPaused ? t('queue_resumed', 'Queue resumed') : t('queue_paused', 'Queue paused'));
      queryClient.invalidateQueries({ queryKey: ['queue-settings'] });
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e, t('queue_state_failed', 'Failed to update queue state'))),
  });

  // Publish now mutation
  const publishNowMutation = useMutation({
    mutationFn: (postId: string) => {
      setPublishingId(postId);
      return queueAPI.publishNow(postId);
    },
    onSuccess: () => {
      toast.success(t('queue_publishing_initiated', 'Publishing initiated'));
      setPublishingId(null);
      queryClient.invalidateQueries({ queryKey: ['queue-posts'] });
    },
    onError: (e: unknown) => {
      toast.error(getErrorMessage(e, t('queue_publish_failed', 'Failed to publish')));
      setPublishingId(null);
    },
  });

  // Remove mutation
  const removeMutation = useMutation({
    mutationFn: (postId: string) => {
      setRemovingId(postId);
      return queueAPI.removePost(postId);
    },
    onSuccess: () => {
      toast.success(t('queue_post_removed', 'Post removed from queue'));
      setRemovingId(null);
      queryClient.invalidateQueries({ queryKey: ['queue-posts'] });
    },
    onError: (e: unknown) => {
      toast.error(getErrorMessage(e, t('queue_remove_failed', 'Failed to remove post')));
      setRemovingId(null);
    },
  });

  // Shuffle mutation
  const shuffleMutation = useMutation({
    mutationFn: () => queueAPI.shuffle(),
    onSuccess: (resp) => {
      toast.success(t('queue_shuffled_posts', 'Shuffled {{count}} posts', { count: resp?.data?.shuffled ?? 0 }));
      queryClient.invalidateQueries({ queryKey: ['queue-posts'] });
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e, t('queue_shuffle_failed', 'Failed to shuffle queue'))),
  });

  const posts = useMemo(() => postsData?.posts || [], [postsData]);
  const queuedCount = posts.filter((p) => p.status === 'scheduled').length;
  const isPaused = settings?.paused ?? false;
  const tzLabel = settings?.timezone
    ? (TIMEZONES.find((tz) => tz.value === settings.timezone)?.label || settings.timezone)
    : '';

  // Group posts by date
  const groupedPosts = useMemo(() => {
    const groups: Record<string, QueuePost[]> = {};
    posts.forEach((post) => {
      const date = post.scheduled_at ? post.scheduled_at.split('T')[0] : 'unscheduled';
      if (!groups[date]) { groups[date] = []; }
      groups[date].push(post);
    });

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const todayStr = today.toISOString().split('T')[0];
    const tomorrowStr = tomorrow.toISOString().split('T')[0];

    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, datePosts]) => {
        let label: string;
        if (date === todayStr) {
          label = t('queue_today', 'Today');
        } else if (date === tomorrowStr) {
          label = t('queue_tomorrow', 'Tomorrow');
        } else if (date === 'unscheduled') {
          label = t('queue_unscheduled', 'Unscheduled');
        } else {
          const d = new Date(date + 'T00:00:00');
          label = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        }
        return { date, label, posts: datePosts };
      });
  }, [posts, t]);

  if (settingsLoading || postsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="flex bg-white h-full">
      {/* Left: Settings sidebar */}
      {settings && (
        <SettingsSidebar
          settings={settings}
          onSave={(updates) => saveSettingsMutation.mutate(updates)}
          isSaving={saveSettingsMutation.isPending}
        />
      )}

      {/* Right: Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Breadcrumb / top bar */}
        <div className="sticky top-0 z-30 bg-white border-b border-slate-100 px-6 h-11 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[13px]">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-slate-500 hover:text-slate-900"
            >
              {t('queue_breadcrumb_home', 'Home')}
            </button>
            <span className="text-slate-300">/</span>
            <span className="text-slate-900 font-medium">{t('queue_breadcrumb', 'Queue')}</span>
          </div>
          <button
            type="button"
            onClick={() => navigate('/compose')}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[12px] font-medium text-white hover:opacity-90"
            style={{ backgroundColor: pc }}
          >
            <Plus className="w-3.5 h-3.5" />
            {t('queue_add_post', 'Add Post')}
          </button>
        </div>

        {/* Meta row */}
        <div className="px-6 pt-5 pb-3 border-b border-slate-100">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-600">
              <span
                aria-hidden="true"
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: isPaused ? '#f59e0b' : '#10b981' }}
              />
              {isPaused ? t('queue_status_paused', 'Paused') : t('queue_status_active', 'Active')}
            </span>
            <span className="text-slate-300 text-[12px]">·</span>
            <span className="text-[12px] text-slate-500 tabular-nums">
              {t('queue_queued_count', '{{count}} queued', { count: queuedCount })}
            </span>
            {tzLabel && (
              <>
                <span className="text-slate-300 text-[12px]">·</span>
                <span className="text-[12px] text-slate-500">{tzLabel}</span>
              </>
            )}

            <div className="flex items-center gap-0.5 ml-auto">
              <button
                type="button"
                onClick={() => togglePauseMutation.mutate(isPaused)}
                disabled={togglePauseMutation.isPending}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[12px] text-slate-600 hover:text-slate-900 hover:bg-slate-50 disabled:opacity-50"
              >
                {togglePauseMutation.isPending ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : isPaused ? (
                  <Play className="w-3 h-3" />
                ) : (
                  <Pause className="w-3 h-3" />
                )}
                {isPaused ? t('queue_resume', 'Resume') : t('queue_pause', 'Pause')}
              </button>
              <button
                type="button"
                onClick={() => shuffleMutation.mutate()}
                disabled={shuffleMutation.isPending || queuedCount < 2}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[12px] text-slate-600 hover:text-slate-900 hover:bg-slate-50 disabled:opacity-40"
              >
                {shuffleMutation.isPending ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Shuffle className="w-3 h-3" />
                )}
                {t('queue_shuffle', 'Shuffle')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/bulk')}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[12px] text-slate-600 hover:text-slate-900 hover:bg-slate-50"
              >
                <Calendar className="w-3 h-3" />
                {t('queue_bulk', 'Bulk')}
              </button>
            </div>
          </div>
        </div>

        {/* Posts list */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isPaused && (
            <div className="flex items-center gap-2 px-3 py-2 mb-4 border-l-2 border-amber-400 bg-amber-50/50 text-[12px] text-amber-800">
              <span className="flex-1">
                {t('queue_paused_warning', 'Queue paused — no posts will auto-publish.')}
              </span>
              <button
                type="button"
                onClick={() => togglePauseMutation.mutate(true)}
                className="text-[12px] font-medium text-amber-900 hover:text-amber-700 underline"
              >
                {t('queue_resume', 'Resume')}
              </button>
            </div>
          )}

          {posts.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="w-5 h-5 text-slate-300 mx-auto mb-3" />
              <p className="text-[13px] text-slate-600 mb-1">
                {t('queue_empty_title', 'Queue is empty')}
              </p>
              <p className="text-[12px] text-slate-400 mb-4">
                {t('queue_empty_message', 'Create posts and schedule them to populate your queue.')}
              </p>
              <button
                type="button"
                onClick={() => navigate('/compose')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[12px] font-medium text-white hover:opacity-90"
                style={{ backgroundColor: pc }}
              >
                <Plus className="w-3.5 h-3.5" />
                {t('queue_create_post', 'Create Post')}
              </button>
            </div>
          ) : (
            <div>
              {groupedPosts.map((group) => (
                <DateGroup
                  key={group.date}
                  dateKey={group.date}
                  label={group.label}
                  count={group.posts.length}
                >
                  {group.posts.map((post) => (
                    <PostRow
                      key={post.id}
                      post={post}
                      pc={pc}
                      onPublishNow={() => publishNowMutation.mutate(post.id)}
                      onRemove={() => removeMutation.mutate(post.id)}
                      isPublishing={publishingId === post.id}
                      isRemoving={removingId === post.id}
                    />
                  ))}
                </DateGroup>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QueuePage;
